
import { useState } from 'react';

interface MagnetCardProps {
  title: string;
  description: string;
  icon: string;
  fileSize: string;
  pages: string;
  downloadUrl: string;
  image: string;
}

export default function MagnetCard({ title, description, icon, fileSize, pages, downloadUrl, image }: MagnetCardProps) {
  const [email, setEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Subscribe to newsletter
      const { supabase } = await import('../../../lib/supabase');
      await supabase.from('newsletter_subscribers').insert([{ email, source: `Lead Magnet: ${title}` }]);

      // Track download
      await supabase.from('analytics_events').insert([{
        event_type: 'lead_magnet_download',
        event_data: { magnet: title, email }
      }]);

      setSubmitted(true);
      
      // Trigger download
      setTimeout(() => {
        window.open(downloadUrl, '_blank');
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 text-white">
          <i className={`${icon} text-3xl`}></i>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm mb-4">{description}</p>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
          <span className="flex items-center gap-1">
            <i className="ri-file-pdf-line"></i>
            {fileSize}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-file-list-line"></i>
            {pages}
          </span>
        </div>

        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
          >
            Download Free Guide
          </button>
        ) : submitted ? (
          <div className="text-center py-3">
            <i className="ri-check-line text-3xl text-emerald-600 mb-2"></i>
            <p className="text-sm text-gray-600">Download starting...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-600 text-white py-2 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 whitespace-nowrap text-sm"
            >
              {isSubmitting ? 'Processing...' : 'Get Free Download'}
            </button>
            <p className="text-xs text-gray-500 text-center">
              We'll send you helpful relocation tips (max 1 email/week)
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
